import { Permissions } from "@/common/Permissions";
import { inactiveEncounterStatus } from "@/types/emr/encounter/encounter";

export const ENCOUNTER_CLINICAL_WRITE_QUESTIONNAIRES = new Set([
  "allergy_intolerance",
  "charge_item",
  "diagnosis",
  "files",
  "medication_request",
  "medication_statement",
  "service_request",
  "symptom",
]);

export const ENCOUNTER_DETAIL_WRITE_QUESTIONNAIRES = new Set(["encounter"]);

export function getEncounterWorkAreaCapabilities(permissions: Permissions) {
  return {
    canViewEncounterWorkArea:
      permissions.canReadEncounterClinicalData ||
      permissions.canWriteEncounterClinicalData ||
      permissions.canSubmitEncounterQuestionnaire,
    canManageEncounterActions: permissions.canWriteEncounter,
  };
}

export function getEncounterDiscoveryCapabilities(permissions: Permissions) {
  return {
    canViewEncounterHistory:
      permissions.canListEncounters ||
      permissions.canReadEncounter ||
      permissions.canReadEncounterClinicalData ||
      permissions.canViewPatients,
    canCreateEncounter: permissions.canCreateEncounter,
  };
}

export interface EncounterPermissionScopeInput {
  facilityId?: string;
  selectedEncounterId?: string;
  primaryEncounterId?: string;
  selectedEncounterStatus?: string;
  primaryEncounterStatus?: string;
  selectedEncounterPermissions: Permissions;
  primaryEncounterPermissions: Permissions;
  patientPermissions: Permissions;
}

export function getEncounterPermissionScopes({
  facilityId,
  selectedEncounterId,
  primaryEncounterId,
  selectedEncounterStatus,
  primaryEncounterStatus,
  selectedEncounterPermissions,
  primaryEncounterPermissions,
}: EncounterPermissionScopeInput) {
  const selectedEncounterIsPrimary =
    !!selectedEncounterId &&
    !!primaryEncounterId &&
    selectedEncounterId === primaryEncounterId;
  const selectedEncounterIsActive =
    !!selectedEncounterStatus &&
    !inactiveEncounterStatus.includes(selectedEncounterStatus);
  const primaryEncounterIsActive =
    !!primaryEncounterStatus &&
    !inactiveEncounterStatus.includes(primaryEncounterStatus);
  const isFacilityScoped = !!facilityId;

  const canReadSelectedEncounter =
    selectedEncounterPermissions.canReadEncounter;
  const canReadClinicalData =
    selectedEncounterPermissions.canReadEncounterClinicalData;
  const canWriteSelectedEncounter =
    selectedEncounterPermissions.canWriteEncounter &&
    isFacilityScoped &&
    selectedEncounterIsPrimary &&
    selectedEncounterIsActive;
  const canWritePrimaryEncounter =
    primaryEncounterPermissions.canReadEncounter &&
    primaryEncounterPermissions.canWriteEncounter &&
    isFacilityScoped &&
    primaryEncounterIsActive;
  const canWriteClinicalData =
    canReadClinicalData &&
    selectedEncounterPermissions.canWriteEncounterClinicalData &&
    isFacilityScoped &&
    selectedEncounterIsPrimary &&
    selectedEncounterIsActive;
  const canSubmitEncounterQuestionnaire =
    selectedEncounterPermissions.canSubmitEncounterQuestionnaire &&
    isFacilityScoped &&
    selectedEncounterIsPrimary &&
    selectedEncounterIsActive;

  return {
    canReadSelectedEncounter,
    canReadClinicalData,
    canWriteSelectedEncounter,
    canWritePrimaryEncounter,
    canWriteClinicalData,
    canSubmitEncounterQuestionnaire,
  };
}

export function getRequiredEncounterQuestionnaireScope(
  questionnaireSlug?: string,
) {
  if (!questionnaireSlug) {
    return "questionnaire";
  }
  if (ENCOUNTER_DETAIL_WRITE_QUESTIONNAIRES.has(questionnaireSlug)) {
    return "details";
  }
  if (ENCOUNTER_CLINICAL_WRITE_QUESTIONNAIRES.has(questionnaireSlug)) {
    return "clinical";
  }
  return "questionnaire";
}
